import { FloatingPortal, offset, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import { Popover } from '@headlessui/react'
import classNames from 'classnames'
import { Calendar as CalendarIcon } from 'iconoir-react'
import moment, { Moment } from 'moment'
import { useEffect, useState } from 'react'
import { useField } from 'remix-validated-form'
import CalendarPicker from '../marketing/CalendarPicker'
import FormHiddenInput from './FormHiddenInput'
import FormInputWrapper, { BaseInputClasses, FormInputWrapperProps } from './FormInputWrapper'
import { formatDate } from 'app/date.utils'

export default function FormDatePicker({
  name,
  label,
  className,
  wrapperClassName,
  labelClassName,
  disabled,
  readOnlyAsLabel,
  cols,
  value,
  placeholder = `Select Date`,
  allowPastDates = true,
  allowFutureDates = true,
  minDate,
  onChange,
}: FormInputWrapperProps<HTMLInputElement> & {
  value?: string | null
  labelClassName?: string
  wrapperClassName?: string
  readOnlyAsLabel?: boolean
  allowFutureDates?: boolean
  onChange?: (value: string) => void
  allowPastDates?: boolean
  placeholder?: string
  minDate?: Date
}) {
  const inputClasses = classNames(
    className,
    BaseInputClasses,
    disabled &&
      (readOnlyAsLabel
        ? `border-default shadow-elevation-1 read-only:cursor-not-allowed read-only:border-none read-only:p-0 read-only:shadow-none read-only:text-subtle read-only:focus:text-subtle`
        : `border-default shadow-elevation-1 read-only:cursor-not-allowed read-only:border-default read-only:bg-surface-muted read-only:text-subtle read-only:focus:text-subtle`),
  )
  const { error } = useField(name)
  const [date, setDate] = useState<Moment | undefined>(value ? moment.utc(value) : undefined)
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [placement, setPlacement] = useState<`bottom` | `top`>(`bottom`)

  // Sync when the value prop arrives after mount or changes. Read as UTC so an
  // ISO timestamp keeps its calendar day.
  useEffect(() => {
    setDate(value ? moment.utc(value) : undefined)
  }, [value])

  // Calendar requires approximately 460px height
  const CALENDAR_MIN_HEIGHT = 460

  // Determine best placement when opening
  useEffect(() => {
    if (isOpen && referenceElement) {
      const rect = referenceElement.getBoundingClientRect()
      const viewportHeight = window.innerHeight

      // Check space below
      const spaceBelow = viewportHeight - rect.bottom - 5 // 5px offset
      // Check space above
      const spaceAbove = rect.top - 5 // 5px offset

      // Choose placement based on available space
      if (spaceBelow >= CALENDAR_MIN_HEIGHT) {
        setPlacement(`bottom`)
      } else if (spaceAbove >= CALENDAR_MIN_HEIGHT) {
        setPlacement(`top`)
      } else {
        // Default to bottom if neither has enough space
        // (will show with scroll)
        setPlacement(`bottom`)
      }
    }
  }, [isOpen, referenceElement])

  const floatingData = useFloating({
    open: isOpen,
    placement: placement === `bottom` ? `bottom-end` : `top-end`,
    strategy: `fixed`,
    elements: { reference: referenceElement, floating: popperElement },
    onOpenChange: setIsOpen,
    // No auto-update to prevent jumping
    middleware: [offset(5)],
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context),
  ])

  const handleDateChange = (selectedDate: Date) => {
    // react-calendar emits local midnight. Keep the picked calendar day in UTC so it
    // round-trips without a viewer-timezone shift.
    const day = moment(selectedDate).format(`YYYY-MM-DD`)
    setDate(moment.utc(day))
    setIsOpen(false)
    onChange?.(day)
  }

  return (
    <FormInputWrapper {...{ name, label, error, cols, className: labelClassName ?? `text-ui-sm`, wrapperClassName }}>
      <FormHiddenInput name={name} value={date?.utc().format(`YYYY-MM-DD`) ?? ``} />
      <Popover className="group relative">
        <Popover.Button
          ref={setReferenceElement}
          className={classNames(inputClasses, `flex items-center justify-between border`)}
          disabled={disabled}
          {...getReferenceProps()}
          onClick={() => setIsOpen(!isOpen)}
        >
          <CalendarIcon className=" h-4 w-4" />
          <div className="ml-4 flex grow items-center">
            <span>{date ? formatDate({ value: date, dateOnly: true }) : placeholder}</span>
          </div>
        </Popover.Button>
        {isOpen && (
          <FloatingPortal>
            <Popover.Panel
              ref={setPopperElement}
              className="z-50 h-fit w-80 rounded-lg border border-default bg-surface-raised p-2 shadow-elevation-2"
              style={floatingData.floatingStyles}
              {...getFloatingProps()}
              static
            >
              <CalendarPicker
                date={date?.format()}
                onDateChange={handleDateChange}
                limitToMonth={false}
                allowPastDates={allowPastDates}
                allowFutureDates={allowFutureDates}
                minDate={minDate}
              />
            </Popover.Panel>
          </FloatingPortal>
        )}
      </Popover>
    </FormInputWrapper>
  )
}
